import { InjectionToken } from '@angular/core';

export const ACCESS_MANAGEMENT_FRONTEND_AUTH_SERVICE: InjectionToken<string> =
  new InjectionToken<string>('ACCESS_MANAGEMENT_FRONTEND_AUTH_SERVICE');

/** Why a session ended. Drives the audit entry written on the way out. */
export type LogoutReason = 'user-initiated' | 'inactivity-timeout';

/**
 * The slice of AuthenticationService the session timeout needs (EDUS-2657).
 *
 * Injected through a token rather than the concrete class so callers — and
 * their unit tests — do not transitively import Firebase. AuthenticationService
 * pulls in `@angular/fire/auth` → `rxfire`, which cannot be loaded under
 * vitest; that is why several specs sit in the exclude list in
 * `vitest.config.ts`.
 */
export interface SessionLogoutHandler {
  logout(reason?: LogoutReason): Promise<void>;
}

export const SESSION_LOGOUT_HANDLER = new InjectionToken<SessionLogoutHandler>(
  'SESSION_LOGOUT_HANDLER'
);

/**
 * Navigation that deliberately bypasses the Angular router (EDUS-2657).
 *
 * A security logout must not be cancellable, and the administration edit
 * screens register `canDeactivate` guards that open an "unsaved changes — stay
 * on page?" confirmation. A router navigation can be vetoed there; a document
 * navigation cannot. It also discards every cached slice of NgRx state, which
 * is what satisfies AC 2.1's "wipe cached in-memory user privileges" — the app
 * has no store-reset meta-reducer.
 *
 * Behind a token so the effect can be tested without a real page navigation.
 */
export interface HardNavigator {
  assign(url: string): void;
  reload(): void;
}

export const HARD_NAVIGATOR = new InjectionToken<HardNavigator>(
  'HARD_NAVIGATOR',
  {
    providedIn: 'root',
    factory: () => ({
      assign: (url: string) => window.location.assign(url),
      reload: () => window.location.reload(),
    }),
  }
);
